import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  ArrowLeft,
  Upload,
  Sparkles,
  X,
  Loader2,
  FileText,
  ImageIcon,
  CheckCircle2,
  AlertCircle,
  Clock,
  FileSpreadsheet,
  FileArchive,
} from "lucide-react";
import { toast } from "sonner";
import ImportConfirmDialog from "@/components/ImportConfirmDialog";

const MAX_FILES = 30;

// Iter 31p: estrae la data ISO dal nome file. Pattern: YYYY-MM-DD o DD-MM-YYYY
// (separatori: '-', '_', '.'). Ritorna null se non rilevata.
function detectDateFromFilename(name) {
  if (!name) return null;
  const cleaned = name.replace(/\.[a-z]+$/i, "");
  // Pattern 1: YYYY[_/-]MM[_/-]DD
  let m = cleaned.match(/(20\d{2})[_\-.](\d{1,2})[_\-.](\d{1,2})/);
  if (m) {
    const y = m[1], mm = String(m[2]).padStart(2, "0"), dd = String(m[3]).padStart(2, "0");
    if (Number(mm) >= 1 && Number(mm) <= 12 && Number(dd) >= 1 && Number(dd) <= 31) {
      return `${y}-${mm}-${dd}`;
    }
  }
  // Pattern 2: DD[_/-]MM[_/-]YYYY
  m = cleaned.match(/(\d{1,2})[_\-.](\d{1,2})[_\-.](20\d{2})/);
  if (m) {
    const y = m[3], mm = String(m[2]).padStart(2, "0"), dd = String(m[1]).padStart(2, "0");
    if (Number(mm) >= 1 && Number(mm) <= 12 && Number(dd) >= 1 && Number(dd) <= 31) {
      return `${y}-${mm}-${dd}`;
    }
  }
  return null;
}

function fileIcon(file) {
  const n = (file.name || "").toLowerCase();
  if (file.type === "application/pdf" || n.endsWith(".pdf"))
    return <FileText className="h-5 w-5 text-red-600" />;
  if (n.endsWith(".xlsx") || n.endsWith(".xls") || n.endsWith(".xlsm"))
    return <FileSpreadsheet className="h-5 w-5 text-emerald-600" />;
  if (n.endsWith(".zip") || n.endsWith(".rar") || n.endsWith(".7z"))
    return <FileArchive className="h-5 w-5 text-amber-600" />;
  return <ImageIcon className="h-5 w-5 text-blue-600" />;
}

function formatBytes(b) {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

export default function ImportPage() {
  const fileInputRef = useRef(null);
  const dropRef = useRef(null);
  const [files, setFiles] = useState([]); // { file, status: 'pending'|'ok'|'error', result?: {} }
  const [running, setRunning] = useState(false);
  const [summary, setSummary] = useState(null);
  const [pdfMode, setPdfMode] = useState("per-page"); // 'per-page' | 'single'
  // Preview/confirm flow
  const [previewData, setPreviewData] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [commesse, setCommesse] = useState([]);

  useEffect(() => {
    // Pre-load anagrafica per il dialog di conferma
    api.get("/employees").then((r) => setEmployees(r.data || []));
    api.get("/commesse").then((r) => setCommesse(r.data || []));
  }, []);

  const reloadAnagrafica = async () => {
    const [e, c] = await Promise.all([
      api.get("/employees"),
      api.get("/commesse"),
    ]);
    setEmployees(e.data || []);
    setCommesse(c.data || []);
  };

  const onSelectFiles = (incoming) => {
    const arr = Array.from(incoming || []);
    if (!arr.length) return;
    if (files.length + arr.length > MAX_FILES) {
      toast.error(`Massimo ${MAX_FILES} file per batch`);
      return;
    }
    setFiles([...files, ...arr.map((f) => ({
      _uid: (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`,
      file: f,
      status: "pending",
    }))]);
  };

  const removeAt = (idx) => setFiles(files.filter((_, i) => i !== idx));
  const clearAll = () => {
    setFiles([]);
    setSummary(null);
  };

  const onDrop = (e) => {
    e.preventDefault();
    dropRef.current?.classList.remove("border-[#0044FF]");
    onSelectFiles(e.dataTransfer.files);
  };

  const handleImport = async () => {
    if (!files.length) return;
    setRunning(true);
    setSummary(null);
    setFiles((prev) => prev.map((f) => ({ ...f, status: "pending" })));
    try {
      const fd = new FormData();
      files.forEach(({ file }) => fd.append("files", file));
      // PREVIEW: auto_save=false so the user can confirm new commesse & ambiguous employees
      const { data } = await api.post(
        `/rapportini/import?pdf_mode=${pdfMode}&auto_save=false`,
        fd,
        {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 900000,
        }
      );
      // If everything is clean and there are zero new commesse / ambiguous employees,
      // open the dialog anyway so the user gives the explicit go-ahead.
      setPreviewData(data);
      toast.info("Anteprima pronta — rivedi e conferma.");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Anteprima non riuscita";
      toast.error(typeof msg === "string" ? msg : "Anteprima non riuscita");
    } finally {
      setRunning(false);
    }
  };

  const handlePreviewConfirmed = (savedSummary) => {
    // Update file rows from saved summary (best effort)
    const ok = savedSummary?.created || 0;
    const err = savedSummary?.errors || 0;
    const skip = savedSummary?.skipped || 0;
    setFiles((prev) =>
      prev.map((f) => ({
        ...f,
        status: err === 0 ? "ok" : (ok > 0 ? "ok" : "error"),
        result: { created: ok, failed: err, skipped: skip },
      }))
    );
    setSummary(savedSummary);
    setPreviewData(null);
  };

  const okCount = files.filter((f) => f.status === "ok").length;
  const errCount = files.filter((f) => f.status === "error").length;
  const progress = files.length === 0 ? 0 : Math.round(((okCount + errCount) / files.length) * 100);

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-8 py-6 space-y-6">
      <ImportConfirmDialog
        open={!!previewData}
        previewData={previewData}
        employees={employees}
        commesse={commesse}
        onCancel={() => setPreviewData(null)}
        onCompleted={handlePreviewConfirmed}
        onAnagraficaChanged={reloadAnagrafica}
      />
      <div className="flex items-center gap-3">
        <Link to="/">
          <Button variant="outline" size="icon" className="h-11 w-11">
            <ArrowLeft className="h-5 w-5" />
          </Button>
        </Link>
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
            Import massivo · Storico
          </div>
          <h1 className="font-heading font-black text-2xl md:text-3xl tracking-tight">
            Importa rapportini cartacei
          </h1>
        </div>
      </div>

      <Card className="p-5 bg-gradient-to-br from-[#F0F4FF] to-white border-[#0044FF]/20">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-md bg-[#0044FF] text-white grid place-items-center shrink-0">
            <Sparkles className="h-5 w-5" />
          </div>
          <div>
            <div className="font-heading font-bold text-base">
              Carica anni di rapportini in un colpo solo
            </div>
            <p className="text-sm text-slate-600 mt-1">
              Trascina <b>foto (JPG/PNG)</b>, <b>PDF</b>, <b>Excel</b> (xlsx/xls)
              o <b>archivi ZIP/RAR</b>. L&apos;AI riconosce e salva automaticamente
              i rapportini nel database.
            </p>
            <div className="text-xs text-slate-500 mt-2">
              Massimo {MAX_FILES} file per batch · 50 MB l&apos;uno · gli archivi
              vengono estratti automaticamente · elaborazione parallela.
            </div>
          </div>
        </div>
      </Card>

      {/* Modalità PDF/Excel */}
      <Card className="p-3 bg-slate-50 border-slate-200">
        <div className="flex items-start gap-2.5">
          <span className="text-base shrink-0">🤖</span>
          <div className="text-xs text-slate-700">
            <strong className="text-slate-900">Auto-detect attivo.</strong> L&apos;app riconosce
            automaticamente il tipo di rapportino dal file: ogni pagina PDF o foglio
            Excel viene trattato come un rapportino separato (modalità consigliata
            per archivi). Se il file Excel/PDF ha la data nel nome (es.{" "}
            <code className="text-[10px]">Rapportino_2026_06_17_mercoledi.xlsx</code>),
            la data viene estratta automaticamente.
          </div>
        </div>
      </Card>

      <Card
        ref={dropRef}
        onDragOver={(e) => {
          e.preventDefault();
          dropRef.current?.classList.add("border-[#0044FF]");
        }}
        onDragLeave={() => dropRef.current?.classList.remove("border-[#0044FF]")}
        onDrop={onDrop}
        className="p-8 border-2 border-dashed transition-colors text-center cursor-pointer hover:border-[#0044FF]"
        onClick={() => !running && fileInputRef.current?.click()}
        data-testid="import-dropzone"
      >
        <Upload className="h-10 w-10 mx-auto text-slate-400" />
        <div className="font-heading font-bold mt-3">
          Trascina i file qui oppure clicca per scegliere
        </div>
        <div className="text-xs text-slate-500 mt-1">
          Supportati: JPG, PNG, WEBP, PDF, XLSX, XLS, ZIP, RAR
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,application/pdf,.xlsx,.xls,.xlsm,.zip,.rar,.7z,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/zip,application/x-zip-compressed,application/vnd.rar,application/x-rar-compressed"
          multiple
          className="hidden"
          onChange={(e) => onSelectFiles(e.target.files)}
        />
      </Card>

      {files.length > 0 && (() => {
        const pending = files.filter((f) => f.status === "pending");
        if (pending.length === 0) return null;
        const detected = pending
          .map((f) => detectDateFromFilename(f.file.name))
          .filter(Boolean);
        const undetected = pending.length - detected.length;
        const datesSet = [...new Set(detected)].sort();
        const datesPreview = datesSet.slice(0, 6).map((iso) => {
          const [y, m, d] = iso.split("-");
          return `${d}/${m}`;
        }).join(", ");
        const more = datesSet.length > 6 ? ` +${datesSet.length - 6}` : "";
        return (
          <Card
            className="p-4 bg-blue-50 border-blue-200"
            data-testid="import-preview-summary"
          >
            <div className="text-xs uppercase tracking-wide text-blue-800 font-bold mb-1">
              Preview pre-import
            </div>
            <div className="text-sm text-blue-900">
              Sto per importare <strong>{pending.length} file</strong>
              {datesSet.length > 0 && (
                <>
                  {" "}— date rilevate:{" "}
                  <strong>{datesPreview}{more}</strong>{" "}
                  ({datesSet.length} giorn{datesSet.length === 1 ? "o" : "i"} distinti)
                </>
              )}
              {undetected > 0 && (
                <>
                  {" "}— <strong className="text-amber-800">{undetected}</strong>{" "}
                  senza data nel nome (l&apos;AI la cercherà nel contenuto).
                </>
              )}
              .
              <span className="block mt-1 text-xs text-blue-700">
                💡 Per velocizzare, nomina i file come{" "}
                <code>Rapportino_2026_06_17_mercoledi.xlsx</code> — la data
                viene estratta automaticamente, senza chiamare l&apos;AI.
              </span>
            </div>
          </Card>
        );
      })()}

      {files.length > 0 && (
        <Card className="p-0 overflow-hidden">
          <div className="px-5 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
            <div className="font-heading font-bold text-sm">
              {files.length} file in coda
              {summary && (
                <span className="ml-3 text-xs font-normal text-slate-500">
                  · {summary.created} ok
                  {summary.skipped > 0 && ` · ${summary.skipped} saltati (pagine vuote)`}
                  {summary.errors > 0 && ` · ${summary.errors} errori`}
                </span>
              )}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={clearAll}
              disabled={running}
              data-testid="import-clear-button"
            >
              <X className="h-4 w-4 mr-1" /> Svuota
            </Button>
          </div>
          {running && (
            <div className="px-5 py-3 border-b border-slate-200">
              <Progress value={progress} className="h-2" />
              <div className="text-xs text-slate-500 mt-1">
                Elaborazione… {okCount + errCount}/{files.length}
              </div>
            </div>
          )}
          <ul className="divide-y divide-slate-200 max-h-96 overflow-auto">
            {files.map((f, idx) => (
              <li
                key={f._uid}
                data-testid={`import-row-${idx}`}
                className="px-5 py-3 flex items-center gap-3 text-sm"
              >
                {fileIcon(f.file)}
                <div className="flex-1 min-w-0">
                  <div className="font-bold truncate">{f.file.name}</div>
                  <div className="text-xs text-slate-500">
                    {formatBytes(f.file.size)}
                    {/* Iter 31p: preview pre-import - data rilevata dal nome file */}
                    {(() => {
                      const det = detectDateFromFilename(f.file.name);
                      if (f.status !== "pending") return null;
                      if (det) {
                        return (
                          <span
                            className="ml-2 inline-flex items-center gap-1 bg-emerald-50 text-emerald-800 border border-emerald-200 px-1.5 py-0.5 rounded text-[10px] font-semibold"
                            data-testid={`import-detected-date-${idx}`}
                          >
                            📅 Data rilevata: {det}
                          </span>
                        );
                      }
                      // Solo per file XLSX/PDF (le foto no — la data è dentro l'immagine)
                      const nl = (f.file.name || "").toLowerCase();
                      if (nl.endsWith(".xlsx") || nl.endsWith(".xls") || nl.endsWith(".pdf")) {
                        return (
                          <span
                            className="ml-2 inline-flex items-center gap-1 bg-amber-50 text-amber-800 border border-amber-200 px-1.5 py-0.5 rounded text-[10px] font-semibold"
                            data-testid={`import-no-date-${idx}`}
                            title="Nessuna data trovata nel nome file. L'AI la cercherà dentro il contenuto."
                          >
                            🤖 Data da rilevare con AI
                          </span>
                        );
                      }
                      return null;
                    })()}
                    {f.result?.created > 1 && (
                      <span className="ml-2 text-green-700 font-bold">
                        · {f.result.created} rapportini creati
                        {f.result.failed > 0 && (
                          <span className="text-red-600"> · {f.result.failed} falliti</span>
                        )}
                      </span>
                    )}
                    {f.result?.created === 1 && f.result?.giorni > 0 && (
                      <span className="ml-2">
                        · {f.result.giorni} giorn{f.result.giorni === 1 ? "o" : "i"} · {f.result.righe} righe
                      </span>
                    )}
                    {f.result?.error && (
                      <span className="ml-2 text-red-600">
                        · {f.result.error}
                      </span>
                    )}
                  </div>
                </div>
                <div className="shrink-0">
                  {f.status === "pending" && !running && (
                    <Clock className="h-5 w-5 text-slate-400" />
                  )}
                  {f.status === "pending" && running && (
                    <Loader2 className="h-5 w-5 text-[#0044FF] animate-spin" />
                  )}
                  {f.status === "ok" && (
                    <Link to={`/rapportino/${f.result.rapportino_id}`}>
                      <span className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full font-bold">
                        <CheckCircle2 className="h-3 w-3" /> #{f.result.numero}
                      </span>
                    </Link>
                  )}
                  {f.status === "error" && (
                    <span className="inline-flex items-center gap-1 text-xs bg-red-100 text-red-800 px-2 py-1 rounded-full font-bold">
                      <AlertCircle className="h-3 w-3" /> Errore
                    </span>
                  )}
                </div>
                {!running && f.status === "pending" && (
                  <button
                    type="button"
                    onClick={() => removeAt(idx)}
                    className="text-slate-400 hover:text-red-600"
                    data-testid={`import-remove-${idx}`}
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        </Card>
      )}

      <div className="flex justify-end gap-3">
        <Link to="/lista">
          <Button variant="outline" className="h-12">Vedi rapportini</Button>
        </Link>
        <Button
          onClick={handleImport}
          disabled={running || files.length === 0}
          data-testid="import-submit-button"
          className="h-12 bg-[#0044FF] hover:bg-[#0033CC] min-w-[220px]"
        >
          {running ? (
            <>
              <Loader2 className="h-5 w-5 mr-2 animate-spin" />
              Analisi AI in corso…
            </>
          ) : (
            <>
              <Sparkles className="h-5 w-5 mr-2" />
              Analizza con AI
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
